/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components\RestoreTab.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the maintenance module.
────────────────────────────────────────────────────────────────*/


import React, { useState, useRef } from 'react';
import { api } from '../../../../lib/api';
import { RefreshCw, Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import ConfirmationModal from '../../../../components/ui/ConfirmationModal';

export default function RestoreTab() {
    const [file, setFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setError(null);
            setSuccess(null);
        }
    };

    const handleRestoreClick = () => {
        if (!file) {
            setError('Please select a backup file to restore.');
            return;
        }

        setShowConfirmModal(true);
    };

    const confirmRestore = async () => {
        setShowConfirmModal(false);
        setIsUploading(true);
        setError(null);
        setSuccess(null);

        const formData = new FormData();
        formData.append('backup', file);

        try {
            await api.post('/restore', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setSuccess('Restore completed successfully.');
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (err) {
            console.error('Restore failed:', err);
            // Try to parse parsing error message if it's a JSON response
            let errorMessage = 'Restoration failed. Please check logs.';
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }
            setError(errorMessage);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
                <h3 className="text-lg font-medium flex items-center">
                    <RefreshCw className="mr-2 h-5 w-5 text-primary" />
                    Restore Backup
                </h3>
                <p className="text-muted-foreground mt-2 mb-6">
                    Restore system or tenant data from a backup file.
                    <br />
                    <span className="text-amber-500 font-medium">Warning: This operation may overwrite existing data. Please ensure you have a current backup before proceeding.</span>
                </p>

                {error && (
                    <div className="mb-4 p-4 rounded-md bg-destructive/15 text-destructive flex items-center">
                        <AlertCircle className="h-5 w-5 mr-2" />
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-4 p-4 rounded-md bg-green-500/15 text-green-600 flex items-center">
                        <CheckCircle className="h-5 w-5 mr-2" />
                        {success}
                    </div>
                )}

                <div className="flex flex-col gap-4 max-w-md">
                    <div className="flex flex-col gap-2">
                        <label htmlFor="backup-file" className="text-sm font-medium">Select Backup File (.zip)</label>
                        <div className="flex items-center gap-2">
                            <input
                                ref={fileInputRef}
                                id="backup-file"
                                type="file"
                                accept=".zip"
                                onChange={handleFileChange}
                                className="flex w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            />
                        </div>
                        {file && (
                            <p className="text-xs text-muted-foreground flex items-center mt-1">
                                <FileText className="h-3 w-3 mr-1" />
                                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </p>
                        )}
                    </div>

                    <button
                        onClick={handleRestoreClick}
                        disabled={!file || isUploading}
                        className={`
              inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors 
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 
              disabled:opacity-50 disabled:pointer-events-none ring-offset-background
              bg-destructive text-destructive-foreground hover:bg-destructive/90 h-10 py-2 px-4 shadow-sm w-full md:w-auto
            `}
                    >
                        {isUploading ? (
                            <>
                                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                                Restoring...
                            </>
                        ) : (
                            <>
                                <Upload className="mr-2 h-4 w-4" />
                                Start Restore
                            </>
                        )}
                    </button>
                </div>
            </div>

            <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={confirmRestore}
                title="Confirm Restore"
                message="WARNING: This will overwrite existing data for the tenant/domain in the backup. This action cannot be undone. Are you sure you want to proceed?"
                confirmText="Restore"
                cancelText="Cancel"
                type="danger"
            />
        </div>
    );
}
